import { useState, useEffect } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Badge } from 'src/components/ui/badge';
import {
  CircleNotchIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  FloppyDiskIcon,
  PlugIcon,
  ArrowCounterClockwiseIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import {
  useGetIntegration,
  useUpdateIntegration,
  useTestOracleFusionConnection,
  useFusionSyncState,
  useResetFusionSyncState,
} from 'src/api/rpc/hooks/integrations/useIntegrations';
import {
  IntegrationAvailability,
  IntegrationKey,
  OracleFusionIntegrationSettings as OracleFusionSettings,
  type FusionSyncStateDTO,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';

export function OracleFusionIntegrationSettings({
  availability: _availability,
}: {
  availability?: IntegrationAvailability;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const {
    data: integration,
    isLoading,
    error,
  } = useGetIntegration(IntegrationKey.OracleFusion);
  const updateIntegration = useUpdateIntegration();
  const testConnection = useTestOracleFusionConnection();

  const [formData, setFormData] = useState<OracleFusionSettings>({
    baseUrl: '',
    username: '',
    password: '',
    apiVersion: undefined,
    organizationCode: undefined,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    'untested' | 'success' | 'failed'
  >('untested');

  const [hasExistingPassword, setHasExistingPassword] = useState(false);

  useEffect(() => {
    if (integration?.settings) {
      const settings = integration.settings as OracleFusionSettings;
      setFormData({
        ...settings,
        // Never populate the password field with the obfuscated sentinel
        password: '',
      });
      if (settings.password) {
        setHasExistingPassword(true);
      }
    }
  }, [integration]);

  const handleFieldChange = (
    field: keyof OracleFusionSettings,
    value: string,
  ) => {
    setConnectionStatus('untested');
    setFormData((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const handleSave = async () => {
    const needsPassword = !hasExistingPassword && !formData.password;
    if (!formData.baseUrl || !formData.username || needsPassword) {
      showToast({
        title: 'Validation Error',
        message: 'Base URL, username, and password are required.',
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    // Omit password when the user didn't type a new one — the backend
    // will preserve the existing encrypted value.
    const { password, ...rest } = formData;
    const settingsToSend = password ? { ...rest, password } : rest;

    setIsSaving(true);
    try {
      await updateIntegration.mutateAsync({
        key: IntegrationKey.OracleFusion,
        settings: settingsToSend as OracleFusionSettings,
      });
      showToast({
        title: 'Settings Saved',
        message: 'Oracle Fusion connection settings have been saved.',
        color: 'success',
        icon: 'check',
      });
    } catch {
      showToast({
        title: 'Save Failed',
        message: 'Failed to save settings. Please try again.',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    // Save current form values first so the test uses what's on screen
    const needsPassword = !hasExistingPassword && !formData.password;
    if (!formData.baseUrl || !formData.username || needsPassword) {
      showToast({
        title: 'Validation Error',
        message: 'Base URL, username, and password are required.',
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    setIsSaving(true);
    try {
      const { password, ...rest } = formData;
      const settingsToSend = password ? { ...rest, password } : rest;
      const saved = await updateIntegration.mutateAsync({
        key: IntegrationKey.OracleFusion,
        settings: settingsToSend as OracleFusionSettings,
      });

      const result = await testConnection.mutateAsync({
        integrationId: saved.id,
      });
      setConnectionStatus(result.success ? 'success' : 'failed');
      showToast({
        title: result.success ? 'Connection Successful' : 'Connection Failed',
        message: result.message,
        color: result.success ? 'success' : 'error',
        icon: result.success ? 'check' : 'error',
      });
    } catch {
      setConnectionStatus('failed');
      showToast({
        title: 'Connection Test Failed',
        message: 'An unexpected error occurred while testing the connection.',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
        <span>
          <T context="loading message while Oracle Fusion integration settings are fetched">
            Loading integration settings...
          </T>
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <AlertContainer variant="destructive">
        <WarningCircleIcon className="h-4 w-4" />
        <AlertDescription>
          <T context="error message when Oracle Fusion integration settings fail to load">
            Failed to load integration settings. Please try refreshing the page.
          </T>
        </AlertDescription>
      </AlertContainer>
    );
  }

  const isConfigured =
    !!integration?.settings &&
    !!(integration.settings as OracleFusionSettings).baseUrl &&
    !!(integration.settings as OracleFusionSettings).username &&
    hasExistingPassword;

  return (
    <div className="space-y-6">
      {/* Integration Status */}
      <div className="flex items-center gap-3">
        {isConfigured && connectionStatus === 'success' ? (
          <>
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="font-medium">
                <T context="Oracle Fusion integration status label when connection test succeeded">
                  Integration Active
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-green-100 text-green-700 border-green-300"
            >
              <T context="Oracle Fusion integration badge when successfully connected">
                Connected
              </T>
            </Badge>
          </>
        ) : connectionStatus === 'failed' ? (
          <>
            <div className="flex items-center gap-2">
              <WarningCircleIcon className="h-5 w-5 text-red-500" />
              <span className="font-medium">
                <T context="Oracle Fusion integration status label when connection test failed">
                  Connection Failed
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-red-50 text-red-700 border-red-300"
            >
              <T context="Oracle Fusion integration badge prompting user to verify credentials after failure">
                Check Credentials
              </T>
            </Badge>
          </>
        ) : isConfigured ? (
          <>
            <div className="flex items-center gap-2">
              <WarningCircleIcon className="h-5 w-5 text-amber-500" />
              <span className="font-medium">
                <T context="Oracle Fusion integration status when credentials saved but not tested">
                  Credentials Saved
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-300"
            >
              <T context="Oracle Fusion integration badge when connection not yet verified">
                Not Verified
              </T>
            </Badge>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <WarningCircleIcon className="h-5 w-5 text-amber-500" />
              <span className="font-medium">
                <T context="Oracle Fusion integration status when not yet configured">
                  Not Configured
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-300"
            >
              <T context="Oracle Fusion integration badge when user must enter credentials">
                Credentials Required
              </T>
            </Badge>
          </>
        )}
      </div>

      {/* Connection Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <T context="Oracle Fusion integration settings card title">
              Connection Configuration
            </T>
          </CardTitle>
          <CardDescription>
            <T context="Oracle Fusion integration settings card description">
              Configure credentials and connection settings for Oracle Fusion
              Cloud
            </T>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fusion-base-url">
              <T context="form label for Oracle Fusion tenant base URL field">
                Base URL
              </T>
            </Label>
            <Input
              id="fusion-base-url"
              type="url"
              placeholder={gt('https://fa-xxxx.oraclecloud.com', {
                context:
                  'placeholder example Oracle Fusion Cloud hostname URL pattern',
              })}
              value={formData.baseUrl}
              onChange={(e) => handleFieldChange('baseUrl', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fusion-username">
              <T context="form label for Oracle Fusion API username field">
                Username
              </T>
            </Label>
            <Input
              id="fusion-username"
              type="text"
              placeholder={gt('Fusion API username', {
                context: 'placeholder for Oracle Fusion API username input',
              })}
              value={formData.username}
              onChange={(e) => handleFieldChange('username', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fusion-password">
              <T context="form label for Oracle Fusion API password field">
                Password
              </T>
            </Label>
            <Input
              id="fusion-password"
              type="password"
              placeholder={
                hasExistingPassword
                  ? gt('Password saved — enter new value to change', {
                      context:
                        'password field placeholder when a saved password already exists',
                    })
                  : gt('Fusion API password', {
                      context:
                        'password field placeholder when user must enter Oracle Fusion API password',
                    })
              }
              value={formData.password}
              onChange={(e) => handleFieldChange('password', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fusion-api-version">
              <T context="form label for optional Oracle Fusion API version field">
                API Version (optional)
              </T>
            </Label>
            <Input
              id="fusion-api-version"
              type="text"
              placeholder="e.g. 11.13.18.05"
              value={formData.apiVersion ?? ''}
              onChange={(e) => handleFieldChange('apiVersion', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fusion-org-code">
              <T context="form label for optional Oracle Fusion organization code filter">
                Organization Code (optional)
              </T>
            </Label>
            <Input
              id="fusion-org-code"
              type="text"
              placeholder={gt('Filter items by organization code', {
                context:
                  'placeholder explaining optional organization code filters synced data',
              })}
              value={formData.organizationCode ?? ''}
              onChange={(e) =>
                handleFieldChange('organizationCode', e.target.value)
              }
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  <T context="button label while Oracle Fusion settings are saving">
                    Saving...
                  </T>
                </>
              ) : (
                <>
                  <FloppyDiskIcon className="h-4 w-4 mr-2" />
                  <T context="button label to save Oracle Fusion connection settings">
                    Save Settings
                  </T>
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testConnection.isPending || isSaving}
            >
              {testConnection.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  <T context="button label while Oracle Fusion connection test runs">
                    Testing...
                  </T>
                </>
              ) : (
                <>
                  <PlugIcon className="h-4 w-4 mr-2" />
                  <T context="button label to run Oracle Fusion connection test">
                    Test Connection
                  </T>
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sync State Management */}
      {integration?.id && (
        <FusionSyncStateCard integrationId={integration.id} />
      )}
    </div>
  );
}

function formatSyncStatus(status: string) {
  switch (status) {
    case 'success':
      return (
        <Badge
          variant="outline"
          className="bg-green-100 text-green-700 border-green-300"
        >
          <T context="sync status badge for successful sync">Success</T>
        </Badge>
      );
    case 'partial_failure':
      return (
        <Badge
          variant="outline"
          className="bg-amber-50 text-amber-700 border-amber-300"
        >
          <T context="sync status badge for partial failure">Partial</T>
        </Badge>
      );
    case 'failure':
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-300"
        >
          <T context="sync status badge for failed sync">Failed</T>
        </Badge>
      );
    default:
      return (
        <Badge variant="outline">
          <Var>{status}</Var>
        </Badge>
      );
  }
}

function FusionSyncStateCard({ integrationId }: { integrationId: string }) {
  const { showToast } = useToast();
  const { data: syncStates, isLoading } = useFusionSyncState(integrationId);
  const resetSyncState = useResetFusionSyncState();
  const [resettingTypes, setResettingTypes] = useState<Set<string>>(new Set());

  const handleReset = async (integrationType: string) => {
    setResettingTypes((prev) => new Set(prev).add(integrationType));
    try {
      await resetSyncState.mutateAsync({
        integrationId,
        integrationTypes: [integrationType],
      });
      showToast({
        title: 'Cursor Reset',
        message: `Sync cursor for "${integrationType}" has been cleared. The next sync will do a full fetch.`,
        color: 'success',
        icon: 'check',
      });
    } catch {
      showToast({
        title: 'Reset Failed',
        message: 'Failed to reset sync cursor. Please try again.',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setResettingTypes((prev) => {
        const next = new Set(prev);
        next.delete(integrationType);
        return next;
      });
    }
  };

  const handleResetAll = async () => {
    if (!syncStates || syncStates.length === 0) return;
    const allTypes = syncStates.map(
      (s: FusionSyncStateDTO) => s.integrationType,
    );
    setResettingTypes(new Set(allTypes));
    try {
      await resetSyncState.mutateAsync({
        integrationId,
        integrationTypes: allTypes,
      });
      showToast({
        title: 'All Cursors Reset',
        message:
          'All sync cursors have been cleared. The next sync will do a full fetch for all types.',
        color: 'success',
        icon: 'check',
      });
    } catch {
      showToast({
        title: 'Reset Failed',
        message: 'Failed to reset sync cursors. Please try again.',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setResettingTypes(new Set());
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <T context="sync cursors card title">Sync Cursors</T>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CircleNotchIcon className="h-4 w-4 animate-spin" />
            <T context="loading message for sync state">
              Loading sync state...
            </T>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">
              <T context="sync cursors card title">Sync Cursors</T>
            </CardTitle>
            <CardDescription>
              <T context="sync cursors card description">
                Each sync type tracks a cursor so only new/changed records are
                fetched. Resetting a cursor forces the next run to do a full
                sync.
              </T>
            </CardDescription>
          </div>
          {syncStates && syncStates.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetAll}
              disabled={resettingTypes.size > 0}
            >
              {resettingTypes.size > 0 ? (
                <CircleNotchIcon className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <TrashIcon className="h-4 w-4 mr-1" />
              )}
              <T context="button to reset all sync cursors">Reset All</T>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!syncStates || syncStates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            <T context="empty state message when no sync cursors exist">
              No sync cursors yet. Cursors are created after the first sync run.
            </T>
          </p>
        ) : (
          <div className="divide-y">
            {syncStates.map((state: FusionSyncStateDTO) => (
              <div
                key={state.id}
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium font-mono">
                      {state.integrationType}
                    </span>
                    {formatSyncStatus(state.lastSyncStatus)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <T context="sync state timestamp and row count display">
                      Last sync:{' '}
                      <Var>{new Date(state.lastSyncTime).toLocaleString()}</Var>{' '}
                      · <Var>{state.lastSyncRowCount.toLocaleString()}</Var>{' '}
                      rows
                    </T>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleReset(state.integrationType)}
                  disabled={resettingTypes.has(state.integrationType)}
                >
                  {resettingTypes.has(state.integrationType) ? (
                    <CircleNotchIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowCounterClockwiseIcon className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
